import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { 
  ArrowRightIcon,
  BoltIcon,
  BuildingOffice2Icon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  GlobeAltIcon,
  TrophyIcon,
  CheckCircleIcon,
  SunIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  MapPinIcon,
  CalendarIcon,
  PencilSquareIcon,
  PrinterIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const MACHINERY_THUMB = '/assets/machines/bobcattele.jpg';

const Home = () => {
  const { t } = useTranslation();
  const { scrollYProgress } = useScroll();
  const [showMap, setShowMap] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showWhoAreWe, setShowWhoAreWe] = useState(false);

  useEffect(() => {
    const checkConsent = () => {
      const consent = localStorage.getItem('cookieConsent');
      if (consent === 'accepted') {
        setShowMap(true);
      }
    };
    
    // Check on mount
    checkConsent();
    
    // Listen for cookie consent acceptance
    window.addEventListener('cookieConsentAccepted', checkConsent);
    
    return () => {
      window.removeEventListener('cookieConsentAccepted', checkConsent);
    };
  }, []);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') return;
      if (selectedCertificate) {
        setSelectedCertificate(null);
      }
      if (showWhoAreWe) {
        setShowWhoAreWe(false);
      }
    };

    if (selectedCertificate || showWhoAreWe) {
      window.addEventListener('keydown', handleEscape);
    }

    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [selectedCertificate, showWhoAreWe]);

  const heroOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  // Stats
  const stats = [
    { value: '34+', label: t('projectsStat'), icon: BuildingOffice2Icon },
    { value: '400+ MWp', label: t('powerStat'), icon: BoltIcon },
    { value: '8', label: t('countriesStat'), icon: GlobeAltIcon },
    { value: '100+', label: t('workersStat'), icon: ChartBarIcon },
  ];

  // Services
  const services = [
    {
      key: 'design',
      title: t('designTitle'),
      desc: t('designDesc'),
      icon: PencilSquareIcon,
    },
    {
      key: 'construct',
      title: t('constructTitle'),
      desc: t('constructDesc'),
      icon: WrenchScrewdriverIcon,
    },
    {
      key: 'maintain',
      title: t('maintainTitle'),
      desc: t('maintainDesc'),
      icon: CheckCircleIcon,
    },
    {
      key: 'labelPrinting',
      title: t('labelPrintingTitle'),
      desc: t('labelPrintingDesc'),
      icon: PrinterIcon,
    },
  ];

  // In Progress Projects
  const inProgressProjects = [
    { id: 'balkantsi', location: t('projectBalkantsi'), capacity: t('projectBalkantsiCapacity'), image: '/assets/thumbnails/balkantsi0206.jpg' },
    { id: 'ihtiman', location: t('projectIhtiman'), capacity: t('projectIhtimanCapacity'), image: '/assets/thumbnails/ihtimanprog.jpg' },
    { id: 'morocco', location: t('projectMorocco'), capacity: t('projectMoroccoCapacity'), image: '/assets/thumbnails/morocco0206.jpeg' },
    { id: 'prevancher', location: t('projectPrevancher'), capacity: t('projectPrevancherCapacity'), image: '/assets/thumbnails/Prevancher0206.jpg' },
  ];

  return (
    <div className="text-slate-900 overflow-hidden">
      <Helmet>
        <title>Infinity Energy Solar – {t('home')}</title>
        <meta name="description" content={t('metaDescription')} />
      </Helmet>

      {/* Hero Section - Compact */}
      <section id="home" className="relative min-h-[90vh] flex items-center overflow-hidden pt-24">
        {/* Video Background */}
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/videos/drone.mp4" type="video/mp4" />
        </video>
        
        {/* Overlay gradient - more transparent */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/40 via-blue-900/30 to-indigo-900/40" />
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-blue-600/10 to-transparent" />
        
        {/* Content */}
        <div className="relative z-20 w-full px-6 lg:px-16 py-16">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-xl sm:text-4xl md:text-7xl lg:text-8xl font-black mb-6 leading-tight sm:leading-[0.9] tracking-tight">
              <span className="block text-white">{t('futureTextLine1')}</span>
              <span className="block text-white">{t('futureTextLine2')}</span>
            </h1>

            <p className="text-xl md:text-2xl text-white/90 mb-12 max-w-xl leading-relaxed font-bold">
              {t('heroSubtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12 items-start">
              <button
                onClick={() => setShowWhoAreWe(true)}
                className="px-8 py-4 border-2 border-white/30 text-white font-bold rounded-lg hover:border-white hover:bg-white/10 transition-colors backdrop-blur-sm"
              >
                {t('whoAreWe')}
              </button>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  const element = document.getElementById('contact');
                  if (element) {
                    const offset = 150;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }}
                className="px-8 py-4 border-2 border-white/30 text-white font-bold rounded-lg hover:border-white hover:bg-white/10 transition-colors backdrop-blur-sm flex items-center gap-2"
              >
                {t('contactUs')}
                <ArrowRightIcon className="w-5 h-5" />
              </a>
              <Link
                to="/projects"
                className="px-8 py-4 border-2 border-white/30 text-white font-bold rounded-lg hover:border-white hover:bg-white/10 transition-colors backdrop-blur-sm flex items-center gap-2"
              >
                {t('exploreProjects')}
                <ArrowRightIcon className="w-5 h-5" />
              </Link>
            </div>

            {/* Inline Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {stats.map((stat, i) => (
                <div
                  key={i}
                  className="bg-white/20 backdrop-blur-xl border border-white/30 rounded-xl md:rounded-2xl p-4 md:p-5 hover:bg-white/30 transition-colors flex flex-col justify-center min-h-0"
                >
                  <div className="text-2xl sm:text-3xl md:text-3xl font-bold text-white mb-2 leading-tight">{stat.value}</div>
                  <div className="text-sm sm:text-base text-white/90 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Wrapper with 3-Color Gradient */}
      <div className="relative bg-gradient-to-b from-green-100 via-yellow-100 to-blue-100">
        {/* Background Images Layer - Above gradient, below grid */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url('/assets/background/solarpark.png'), url('/assets/background/windturbine.png')`,
            backgroundSize: '60% auto, 40% auto',
            backgroundPosition: 'bottom right, top left',
            backgroundRepeat: 'no-repeat',
            opacity: 0.2,
            zIndex: 1
          }}
        />
        {/* Grid Pattern Overlay - On top of images */}
        <div className="grid-pattern relative" style={{ zIndex: 2 }}>
      {/* Projects: In Progress and Upcoming Section */}
      <section id="news" className="py-12 px-6 lg:px-16 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-1 w-16 bg-gradient-to-r from-emerald-400 to-emerald-600" />
              <h2 className="text-xl md:text-5xl font-black text-blue-900">{t('projectsInProgressUpcoming')}</h2>
            </div>
          </motion.div>

          {/* In Progress Projects */}
          <motion.div
            viewport={{ once: true }}
            className="mb-16"
          >
            <h3 className="text-xl md:text-3xl font-bold text-slate-900 mb-8">{t('inProgress')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {inProgressProjects.map((project) => (
                <motion.div
                  key={project.id}
                  viewport={{ once: true }}
                  className="relative rounded-3xl overflow-hidden border border-slate-200 hover:border-emerald-400 transition-all shadow-lg hover:shadow-xl aspect-[4/3]"
                >
                  <img 
                    src={project.image} 
                    alt={project.location}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-100/50 to-emerald-200/50 rounded-bl-3xl" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 right-4 text-right">
                    <p className="text-white font-bold text-lg md:text-xl drop-shadow-lg">
                      {project.location}{project.capacity ? ` ${project.capacity}` : ''}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-12 px-6 lg:px-16 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-1 w-16 bg-gradient-to-r from-emerald-400 to-emerald-600" />
              <h2 className="text-xl md:text-5xl font-black text-blue-900">{t('ourServices')}</h2>
            </div>
            <p className="text-slate-700 text-lg max-w-3xl leading-tight">{t('ourServicesDesc')}</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={service.key}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="group relative bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-slate-200 hover:border-emerald-600 transition-all shadow-lg hover:shadow-xl"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-100/50 to-emerald-200/50 rounded-bl-3xl" />
                <service.icon className="w-12 h-12 text-emerald-500 mb-6" />
                <h3 className="text-2xl font-bold mb-3 text-slate-900">{service.title}</h3>
                <p className="text-slate-700">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Machines Section */}
      <section id="machines" className="py-12 px-6 lg:px-16 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-1 w-16 bg-gradient-to-r from-emerald-400 to-emerald-600" />
              <h2 className="text-xl md:text-5xl font-black text-blue-900">{t('machines')}</h2>
            </div>
            <p className="text-slate-700 text-lg max-w-3xl leading-tight">
              {t('machinesIntro')}
            </p>
          </motion.div>

          <motion.div
            viewport={{ once: true }}
            className="flex w-full flex-col gap-8"
          >
            <div className="group w-full overflow-hidden rounded-3xl border border-slate-200 bg-white/70 shadow-lg backdrop-blur-sm transition-all hover:border-emerald-600 hover:shadow-xl">
              <img
                src={MACHINERY_THUMB}
                alt=""
                className="h-[min(520px,62vh)] w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              />
            </div>

            <div className="flex justify-center">
              <Link
                to="/machines"
                className="group relative inline-flex px-10 py-5 bg-gradient-to-r from-emerald-400 to-emerald-600 text-white font-bold rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2"
                aria-label={t('machinesBrowseGallery')}
              >
                <span className="relative z-10 flex items-center gap-2">
                  {t('machinesBrowseGallery')}
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-emerald-400"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Certificates Section */}
      <section id="certificates" className="py-12 px-6 lg:px-16 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-1 w-16 bg-gradient-to-r from-emerald-400 to-emerald-600" />
              <h2 className="text-xl md:text-5xl font-black text-blue-900">{t('certificates')}</h2>
            </div>
            <p className="text-slate-700 text-lg max-w-3xl leading-tight">
              {t('certificatesIntro')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {[
              {
                key: 'certificate0',
                image: '/certificates/Cert1.jpg',
                title: t('certificate0Title'),
                desc: t('certificate0Desc'),
              },
              {
                key: 'certificate1',
                image: '/certificates/Cert2.jpg',
                title: t('certificate1Title'),
                desc: t('certificate1Desc'),
              },
              {
                key: 'certificate2',
                image: '/certificates/Cert3.jpg',
                title: t('certificate2Title'),
                desc: t('certificate2Desc'),
              },
            ].map((certificate, i) => (
              <motion.div
                key={certificate.key}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -5 }}
                onClick={() => setSelectedCertificate(certificate.image)}
                className="group relative bg-white/70 backdrop-blur-sm rounded-3xl overflow-hidden border border-slate-200 hover:border-emerald-600 transition-all shadow-lg hover:shadow-xl cursor-pointer"
              >
                <div className="aspect-[4/3] bg-slate-50 flex items-center justify-center overflow-hidden">
                  <img
                    src={certificate.image}
                    alt={certificate.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-3 text-slate-900">{certificate.title}</h3>
                  <p className="text-slate-700">{certificate.desc}</p>
                </div>
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-100/50 to-emerald-200/50 rounded-bl-3xl" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-12 px-6 lg:px-16 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-1 w-16 bg-gradient-to-r from-emerald-400 to-emerald-600" />
              <h2 className="text-xl md:text-5xl font-black text-blue-900">{t('projectsHero')}</h2>
            </div>
            <p className="max-w-3xl text-slate-700 text-lg leading-tight">{t('projectsIntro')}</p>
          </motion.div>

          {/* Thumbnails and Button */}
          <div className="mb-12">
            {/* Thumbnails */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              {[
                { src: '/assets/thumbnails/projectMoussoulens.jpg', alt: 'Moussoulens Project', delay: 0.1 },
                { src: '/assets/thumbnails/projectGermanyDiesel.jpg', alt: 'Germany Diesel Project', delay: 0.2 },
                { src: '/assets/thumbnails/projectLapovo.jpg', alt: 'Lapovo Project', delay: 0.3 },
                { src: '/assets/thumbnails/projectMonreale.jpg', alt: 'Monreale Project', delay: 0.4 },
              ].map((thumbnail, i) => (
                <motion.div
                  key={i}
                  viewport={{ once: true }}
                  className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-lg hover:shadow-xl transition-all aspect-[4/3]"
                >
                  <img
                    src={thumbnail.src}
                    alt={thumbnail.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-100/50 to-emerald-200/50 rounded-bl-3xl" />
                </motion.div>
              ))}
            </div>

            {/* Button */}
            <div className="flex items-center justify-center">
              <Link
                to="/projects"
                className="group relative px-10 py-5 bg-gradient-to-r from-emerald-400 to-emerald-600 text-white font-bold rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all"
              >
                <span className="relative z-10 flex items-center gap-2">
                  {t('exploreProjects')}
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </span>
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-emerald-600 to-emerald-400"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: 0 }}
                  transition={{ duration: 0.3 }}
                />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Infinity Section */}
      <section id="why-infinity" className="py-12 px-6 lg:px-16 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <motion.div
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="h-1 w-16 bg-gradient-to-r from-emerald-400 to-emerald-600" />
              <h2 className="text-xl md:text-5xl font-black text-blue-900">{t('whyHero')}</h2>
            </div>
            <p className="max-w-3xl text-lg text-slate-700 leading-tight">
              {t('whyIntro')}
            </p>
          </motion.div>

          <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 md:p-12 shadow-lg">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
              {/* Images column on left */}
              <div className="flex-shrink-0 space-y-6">
                <div>
                  <img
                    src="/assets/thumbnails/projectNessebar.jpg"
                    alt="Infinity Energy Solar"
                    className="w-full lg:w-80 h-auto rounded-2xl shadow-md object-cover"
                  />
                </div>
                <div>
                  <img
                    src="/assets/thumbnails/projectStGermain.jpg"
                    alt="Infinity Energy Solar"
                    className="w-full lg:w-80 h-auto rounded-2xl shadow-md object-cover"
                  />
                </div>
              </div>

              {/* Text content wrapping around images */}
              <div className="flex-1 space-y-6">
                {[
                  {
                    key: 'why0',
                    title: t('why0Title'),
                    desc: t('why0Desc'),
                  },
                  {
                    key: 'why1',
                    title: t('why1Title'),
                    desc: t('why1Desc'),
                  },
                  {
                    key: 'why2',
                    title: t('why2Title'),
                    desc: t('why2Desc'),
                  },
                  {
                    key: 'why4',
                    title: t('why4Title'),
                    desc: t('why4Desc'),
                  },
                ].map((block, i) => (
                  <motion.div
                    key={block.key}
                    viewport={{ once: true }}
                    className="pb-6 border-b border-slate-200 last:border-b-0 last:pb-0"
                  >
                    <h3 className="text-xl md:text-2xl font-bold text-slate-900 mb-3">{block.title}</h3>
                    <p className="text-slate-700 text-base md:text-lg leading-relaxed">{block.desc}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 px-6 lg:px-16 scroll-mt-24">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="h-1 w-16 bg-gradient-to-r from-emerald-400 to-emerald-600" />
              <h2 className="text-xl md:text-5xl font-black text-blue-900">{t('contactHero')}</h2>
            </div>
            <p className="max-w-3xl text-lg text-slate-700 leading-tight">
              {t('contactText')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Contact Information */}
            <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-slate-200 shadow-lg hover:shadow-xl transition-all">
              <h3 className="text-2xl font-bold mb-6 text-slate-900">{t('contactTitle')}</h3>
              <div className="space-y-4 text-slate-700">
                <div className="flex items-start gap-3">
                  <MapPinIcon className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{t('contactAddress')}:</p>
                    <p className="text-emerald-500">{t('contactFullAddress')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <PhoneIcon className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{t('contactPhone')}:</p>
                    <a href="tel:+359879275550" className="text-emerald-500 hover:text-emerald-600">+359 879 27 55 50</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <EnvelopeIcon className="w-6 h-6 text-emerald-500 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">{t('contactEmail')}:</p>
                    <a href="mailto:office@infisolarbg.com" className="text-emerald-500 hover:text-emerald-600">office@infisolarbg.com</a>
                  </div>
                </div>
              </div>
            </div>

            {/* Google Maps */}
            <div className="rounded-3xl overflow-hidden shadow-lg">
              {showMap ? (
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5182.499650983414!2d23.465461899999994!3d41.8870833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14ababdc3777e4d9%3A0x2fb29127ccdf6983!2sInfinity%20Energy%20Solar!5e1!3m2!1sen!2sbg!4v1744375129602!5m2!1sen!2sbg"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '400px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Infinity Energy Solar Location"
                  className="w-full"
                />
              ) : (
                <div className="text-center text-slate-600 italic py-20 bg-slate-100 rounded-3xl flex items-center justify-center min-h-[400px]">
                  {t('mapConsentRequired')}
                </div>
              )}
            </div>
          </div>

          {/* Employees Section */}
          <div className="bg-white/70 backdrop-blur-sm rounded-3xl p-8 border border-slate-200 shadow-lg">
            <h3 className="text-2xl font-bold mb-4 text-slate-900">{t('employeesTitle')}</h3>
            <p className="text-slate-700 mb-6">{t('employeesInfo')}</p>
            <div className="space-y-3 text-slate-700">
              <div>
                <p className="font-semibold">{t('contactPhone')}: <a href="tel:+359896762334" className="text-emerald-500 hover:text-emerald-600 font-normal">+359 896 76 23 34</a></p>
              </div>
              <div>
                <p className="font-semibold">{t('contactEmail')}: <a href="mailto:employees@infisolarbg.com" className="text-emerald-500 hover:text-emerald-600 font-normal">employees@infisolarbg.com</a></p>
              </div>
            </div>
          </div>
        </div>
      </section>
        </div>
      </div>

      {/* Certificate Modal */}
      {selectedCertificate && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
          onClick={() => setSelectedCertificate(null)}
        >
          <img 
            src={selectedCertificate} 
            alt="Enlarged certificate" 
            className="max-w-4xl max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setSelectedCertificate(null)}
            className="absolute top-6 right-6 text-white text-4xl font-bold hover:text-gray-300 transition-colors z-10"
          >
            &times;
          </button>
        </div>
      )}

      {/* Who Are We Modal */}
      {showWhoAreWe && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowWhoAreWe(false)}
        >
          <div
            className="bg-white/20 backdrop-blur-xl border border-white/30 rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-8 md:p-12"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-3xl md:text-4xl font-black text-white">{t('whoAreWe')}</h2>
              <button
                onClick={() => setShowWhoAreWe(false)}
                className="text-white/80 hover:text-white text-4xl font-bold transition-colors"
              >
                &times;
              </button>
            </div>
            <div className="prose prose-lg max-w-none">
              <p className="text-white/90 leading-relaxed whitespace-pre-line">
                {t('whoAreWeText')}
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Home;
